import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useTourContext } from './TourProvider';

export function TourSheet() {
  const {
    currentStep,
    currentStepData,
    totalSteps,
    nextStep,
    prevStep,
    closeTour,
    finishTour,
    dismissGuide,
  } = useTourContext();
  
  const sheetRef = useRef<HTMLDivElement>(null);
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  // Focus trap and keyboard navigation
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;

    const focusableElements = sheet.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      } else if (e.key === 'Escape') {
        closeTour();
      } else if (e.key === 'ArrowRight' && !isLastStep) {
        nextStep();
      } else if (e.key === 'ArrowLeft' && !isFirstStep) {
        prevStep();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeTour, nextStep, prevStep, isFirstStep, isLastStep]);

  if (!currentStepData) return null;

  return (
    <motion.div
      ref={sheetRef}
      initial={{ opacity: 0, y: 100 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 100 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="fixed bottom-0 left-0 right-0 z-[10000] bg-card border-t border-border rounded-t-2xl shadow-2xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-title-mobile"
      aria-describedby="tour-description-mobile"
    >
      {/* Drag handle */}
      <div className="flex justify-center pt-3 pb-1">
        <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
      </div>

      {/* Close button */}
      <button
        onClick={closeTour}
        className="absolute top-3 right-3 p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors"
        aria-label="Close tour"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Content */}
      <div className="px-6 pt-2 pb-4">
        <h3 
          id="tour-title-mobile"
          className="text-xl font-semibold text-foreground mb-2"
        >
          {currentStepData.title}
        </h3>
        <p 
          id="tour-description-mobile"
          className="text-sm text-muted-foreground leading-relaxed"
        >
          {currentStepData.message}
        </p>
      </div>

      {/* Step dots */}
      <div className="flex justify-center gap-2 pb-4">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div
            key={i}
            className={`w-2 h-2 rounded-full transition-colors ${
              i === currentStep 
                ? 'bg-tenacity-cyan' 
                : 'bg-muted-foreground/30'
            }`}
          />
        ))}
      </div>

      {/* Navigation buttons */}
      <div className="flex items-center justify-between px-6 pb-4">
        <Button
          variant="ghost"
          size="default"
          onClick={prevStep}
          disabled={isFirstStep}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </Button>

        {isLastStep ? (
          <Button
            size="default"
            onClick={finishTour}
            className="bg-tenacity-cyan text-primary-foreground hover:bg-tenacity-cyan/90"
          >
            Finish
          </Button>
        ) : (
          <Button
            size="default"
            onClick={nextStep}
            className="bg-tenacity-cyan text-primary-foreground hover:bg-tenacity-cyan/90"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>

      {/* Don't show again option */}
      <div className="border-t border-border px-6 py-4 pb-6">
        <label className="flex items-center gap-3 cursor-pointer text-sm text-muted-foreground hover:text-foreground transition-colors">
          <Checkbox 
            id="dismiss-tour-mobile"
            onCheckedChange={(checked) => {
              if (checked) {
                dismissGuide();
              }
            }}
          />
          <span>Don't show this guide again</span>
        </label>
      </div>
    </motion.div>
  );
}
